/**
 * Almari canonical schema. Single source of truth.
 * Mirrored in:
 *  - scripts/seed_sample.ts  (sample data generator)
 *  - mcp/server.ts           (MCP resource shape)
 *  - future: ios/Almari/     (App Intent AppEntity)
 */

export const SCHEMA_VERSION = 1 as const;

export const CATEGORIES = [
  'shirts',
  'pants',
  'shoes',
  'ethnic_kurta',
  'ethnic_sherwani',
  'outerwear',
  'accessories',
  'innerwear',
  'sportswear',
] as const;
export type ClothingCategory = (typeof CATEGORIES)[number];

export const TIERS = ['starter', 'curator', 'collector', 'connoisseur', 'tycoon'] as const;
export type Tier = (typeof TIERS)[number];

export const TIER_DISPLAY: Record<Tier, {
  name: string;
  hindi: string;
  tagline: string;
  trajectory: string;
}> = {
  starter: {
    name: 'Starter',
    hindi: 'शुरुआत',
    tagline: 'do shirts, ek kurta — kapda hai, kahaani abhi banni hai',
    trajectory: 'pehla hanger, pehla wasool',
  },
  curator: {
    name: 'Curator',
    hindi: 'क्यूरेटर',
    tagline: 'thodi si almari, thoda sa nakhra — har piece soch ke chuna hua',
    trajectory: 'almari ban rahi hai, awaaz aa rahi hai',
  },
  collector: {
    name: 'Collector',
    hindi: 'कलेक्टर',
    tagline: 'har mausam, har mehfil — kuch na kuch fit hi rakhte ho',
    trajectory: 'archive sambhal rahi hai, kahaani gehri ho rahi hai',
  },
  connoisseur: {
    name: 'Connoisseur',
    hindi: 'पारखी',
    tagline: 'pehchaan kapde se nahi, kapde ki pehchaan aapse hai',
    trajectory: 'cost-per-wear bolta hai — almari portfolio ban gayi hai',
  },
  tycoon: {
    name: 'Tycoon',
    hindi: 'टायकून',
    tagline: 'almari hi flex hai — silai, silk, signature, sab tumhare',
    trajectory: 'curated archive · har taar ki keemat, har dhaage ka raazdaar',
  },
};

export type Source = 'photo_detect' | 'mail_receipt' | 'manual';

export interface ClothingItem {
  id: string;
  name: string;
  brand: string;
  category: ClothingCategory;
  color: string;
  value_inr: number;
  current_value_inr: number;
  acquired_date: string;
  last_worn: string | null;
  wear_count: number;
  cost_per_wear: number;
  source: Source;
}

export interface Insights {
  most_worn: ClothingItem | null;
  dead_weight_value_inr: number;
  dead_weight_count: number;
  top_brand: string | null;
  color_palette: string[];
  avg_cost_per_wear: number;
  wardrobe_age_avg_days: number;
}

export interface CategoryStat {
  count: number;
  value: number;
}

export interface BrandStat {
  count: number;
  value: number;
}

export interface WardrobeSummary {
  schema_version: typeof SCHEMA_VERSION;
  generated_at: string;
  total_items: number;
  total_value_inr: number;
  total_current_value_inr: number;
  tier: Tier;
  by_category: Partial<Record<ClothingCategory, CategoryStat>>;
  by_brand: Record<string, BrandStat>;
  by_color: Record<string, number>;
  items: ClothingItem[];
  insights: Insights;
}

export const CATEGORY_DISPLAY: Record<ClothingCategory, string> = {
  shirts: 'Shirts',
  pants: 'Pants',
  shoes: 'Shoes',
  ethnic_kurta: 'Ethnic — kurta',
  ethnic_sherwani: 'Ethnic — sherwani',
  outerwear: 'Outerwear',
  accessories: 'Accessories',
  innerwear: 'Innerwear',
  sportswear: 'Sportswear',
};

export function tierFor(totalItems: number, totalValueInr: number): Tier {
  if (totalItems >= 300 || totalValueInr >= 2_500_000) return 'tycoon';
  if (totalItems >= 150 || totalValueInr >= 800_000) return 'connoisseur';
  if (totalItems >= 60 || totalValueInr >= 200_000) return 'collector';
  if (totalItems >= 20 || totalValueInr >= 50_000) return 'curator';
  return 'starter';
}

export const formatINR = (n: number): string => {
  const rounded = Math.round(n);
  const s = rounded.toString();
  if (rounded < 1000) return `₹${s}`;
  if (rounded < 100_000) return `₹${rounded.toLocaleString('en-IN')}`;
  if (rounded < 10_000_000)
    return `₹${(rounded / 100_000).toFixed(rounded % 100_000 === 0 ? 0 : 2)}L`;
  return `₹${(rounded / 10_000_000).toFixed(2)}Cr`;
};
